import { useCallback, useEffect, useRef, useState } from "react";
import type { Busy } from "../../net/api.ts";
import { act, read } from "../../net/rally.ts";
import {
  HANDS_OFF_THE_KEYS_AFTER,
  keysTouched,
  READ_THE_LINE_EVERY,
  shouldTell,
} from "../composer/typing.ts";

const NOBODY: Busy = { thinking: [], typing: [], unanswered: [] };

export function useTypist(conversation: string): { typed: () => void; stopped: () => void } {
  const told = useRef<number | null>(null);
  const quiet = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopped = useCallback(() => {
    if (quiet.current !== null) clearTimeout(quiet.current);
    quiet.current = null;
    if (told.current === null) return;
    told.current = null;

    void act("conversation.typing", { conversation, typing: false }).catch(() => {});
  }, [conversation]);

  const typed = useCallback(() => {
    const now = Date.now();

    keysTouched(conversation, now);
    if (shouldTell(told.current, now)) {
      told.current = now;
      void act("conversation.typing", { conversation, typing: true }).catch(() => {});
    }

    if (quiet.current !== null) clearTimeout(quiet.current);
    quiet.current = setTimeout(stopped, HANDS_OFF_THE_KEYS_AFTER);
  }, [conversation, stopped]);

  useEffect(() => stopped, [stopped]);
  return { typed, stopped };
}

export function useBusy(conversation: string): Busy {
  const [busy, setBusy] = useState<Busy>(NOBODY);

  useEffect(() => {
    let stopped = false;

    setBusy(NOBODY);
    const ask = () => {
      void read<Busy>("conversation.busy", { conversation })
        .then((said) => {
          if (!stopped) setBusy(said);
        })

        .catch(() => {
          if (!stopped) setBusy(NOBODY);
        });
    };
    ask();
    const beat = setInterval(ask, READ_THE_LINE_EVERY);
    return () => {
      stopped = true;
      clearInterval(beat);
    };
  }, [conversation]);

  return busy;
}
